import { useState } from "react";
import { isAxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import {
  Account,
  CACHE_KEYS,
  useChangeContraAccount,
  useUpdateAccount,
} from "@rentablo/api/apiv1";
import { GET_ACCOUNTS } from "@api/cacheKeys";

export const useReferenceAccountActions = (depot: Account | undefined) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [isRunning, setIsRunning] = useState(false);
  const [pendingAccountId, setPendingAccountId] = useState<number>();

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: [GET_ACCOUNTS] }),
      queryClient.invalidateQueries({
        queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_ACCOUNTS],
      }),
    ]);

  const showError = (e: unknown) => {
    const message = isAxiosError(e) ? e.response?.data?.message : undefined;
    enqueueSnackbar(message ?? t("form.errors.somethingWentWrong"), {
      variant: "error",
    });
  };

  // Some actions issue several requests in sequence. They must stay pending as
  // one unit: no refetch between the calls (that would render the intermediate
  // server state) and no early re-enabling of the UI in the gap between them.
  // `accountId` marks which row triggered the action, so it can show a spinner
  // while every other control is merely disabled.
  const run = async (operation: () => Promise<void>, accountId?: number) => {
    setIsRunning(true);
    setPendingAccountId(accountId);
    try {
      await operation();
    } catch (e) {
      showError(e);
      throw e;
    } finally {
      await invalidate();
      setIsRunning(false);
      setPendingAccountId(undefined);
    }
  };

  const { mutateAsync: changeContraAccount } = useChangeContraAccount();

  const { mutateAsync: updateAccount } = useUpdateAccount();

  const handleChangePrimary = async (newPrimaryId: number) => {
    if (!depot) return;
    await run(async () => {
      await changeContraAccount({
        id: depot.id,
        contraAccountId: newPrimaryId,
      });
    });
  };

  const handleSetPrimary = async (newPrimaryId: number) => {
    if (!depot) return;
    const oldPrimaryId = depot.contraAccountId;
    await run(async () => {
      await changeContraAccount({
        id: depot.id,
        contraAccountId: newPrimaryId,
      });
      try {
        await updateAccount({
          id: depot.id,
          createOrUpdateAccountParams: {
            name: depot.name,
            type: depot.type,
            additionalContraAccountIdsToRemove: [newPrimaryId],
            ...(oldPrimaryId
              ? { additionalContraAccountIdsToAdd: [oldPrimaryId] }
              : {}),
          },
        });
      } catch (e) {
        if (oldPrimaryId) {
          await changeContraAccount({
            id: depot.id,
            contraAccountId: oldPrimaryId,
          }).catch(() => undefined);
        }
        throw e;
      }
    }, newPrimaryId);
  };

  const handleRemove = async (id: number) => {
    if (!depot) return;
    await run(async () => {
      await updateAccount({
        id: depot.id,
        createOrUpdateAccountParams: {
          name: depot.name,
          type: depot.type,
          additionalContraAccountIdsToRemove: [id],
        },
      });
    });
  };

  const handleAddAdditional = async (ids: number[]) => {
    if (!depot) return;
    await run(async () => {
      await updateAccount({
        id: depot.id,
        createOrUpdateAccountParams: {
          name: depot.name,
          type: depot.type,
          additionalContraAccountIdsToAdd: ids,
        },
      });
    });
  };

  return {
    handleChangePrimary,
    handleSetPrimary,
    handleRemove,
    handleAddAdditional,
    isMutating: isRunning,
    pendingAccountId,
  };
};
